import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Accordion,
  Tab,
  TabPane,
  Icon,
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  Button,
} from 'semantic-ui-react';

import { deleteUser, listUsers } from '@plone/volto/actions/users/users';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';

import { getLocationsManager } from '../../../actions';
import useToast from '../../../helpers/useToast';
import { LocationsTable } from './LocationsTable';
import { UserData } from './UserData';

import jwtDecode from 'jwt-decode';
import { isManager } from '@plone/volto/helpers/User/User';

export const Manager = ({ intl, dispatch, location }) => {
  const token = useSelector((state) => state.userSession.token);
  const users = useSelector((state) => state.users.users);
  const user = useSelector((state) => state.users.user);
  const user_delete = useSelector((state) => state.users.delete);
  const prevuser_delete = usePrevious(user_delete);
  const locations_get = useSelector((state) => state.locationsManager.get);

  const userschema = useSelector((state) => state.userschema);
  const modify = useSelector((state) => state.location.modify);
  const prevmodify = usePrevious(modify);
  const [active, setActive] = useState('');
  const [open, setOpen] = useState(false);

  const userId = token ? jwtDecode(token).sub : '';
  const messages = defineMessages({
    locations: {
      id: 'locations',
      defaultMessage: 'Locations',
    },
    personalData: {
      id: 'personalData',
      defaultMessage: 'Personal data',
    },
    yesDelete: {
      id: 'yesDelete',
      defaultMessage: 'Yes, delete this account permanently',
    },
  });

  useEffect(() => {
    dispatch(listUsers());
    dispatch(getLocationsManager(getBaseUrl(location.pathname)));
  }, [dispatch, user, location.pathname]);

  useEffect(() => {
    //query users after deleting a user
    if (prevuser_delete?.loading && user_delete.loaded) {
      setActive('');
      dispatch(listUsers());
    }
    //query locations after modifying a location
    prevmodify?.loading &&
      modify.loaded &&
      dispatch(getLocationsManager(getBaseUrl(location.pathname)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, modify.loading, user_delete.loading]);

  return (
    <>
      {useToast('location.modify')}
      {useToast('users.delete')}
      <Accordion styled fluid className="accordion-block">
        {users.length > 0 &&
          users
            .filter((user) => user.id !== userId && !isManager(user))
            .map((user, key) => {
              const myLocations = locations_get.data.filter(
                (loc) => loc.user_id === user.id,
              );
              return (
                <div key={key} className="accordion ui fluid styled">
                  <Accordion.Title
                    as={'h3'}
                    active={active === key}
                    aria-expanded={active === key}
                    className={'title accordion-title align-arrow-right'}
                    index={key}
                    onClick={(e) =>
                      active === key ? setActive('') : setActive(key)
                    }
                    onKeyDown={(e) => {
                      if (e.keyCode === 13 || e.keyCode === 32) {
                        e.preventDefault();
                        active === key ? setActive('') : setActive(key);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <Icon
                      name={active === key ? 'minus' : 'plus'}
                      className="24px"
                    />
                    <span>
                      {user.fullname} - {user.email}
                    </span>
                  </Accordion.Title>

                  <Accordion.Content active={active === key}>
                    <Tab
                      panes={[
                        {
                          menuItem: intl.formatMessage(messages.locations),
                          render: () => (
                            <TabPane>
                              <LocationsTable
                                locations={myLocations || []}
                                dispatch={dispatch}
                                loading={locations_get.loading}
                                isManager={true}
                              />
                            </TabPane>
                          ),
                        },
                        {
                          menuItem: intl.formatMessage(messages.personalData),
                          render: () => (
                            <TabPane>
                              <UserData userschema={userschema} user={user} />
                              <Modal
                                onClose={() => setOpen(false)}
                                onOpen={() => setOpen(true)}
                                open={open}
                                trigger={
                                  <Button className="rejected-color">
                                    <FormattedMessage
                                      id="Delete user"
                                      defaultMessage="Delete user"
                                    />
                                  </Button>
                                }
                              >
                                <ModalHeader>
                                  <FormattedMessage
                                    id="Are you sure you want to delete this account permanently?"
                                    defaultMessage="Are you sure you want to delete this account permanently?"
                                  />{' '}
                                  ({user.fullname} - {user.email})
                                </ModalHeader>
                                <ModalContent>
                                  <FormattedMessage
                                    id="If you delete this account, you will not be able to restore any information saved up today."
                                    defaultMessage="If you delete this account, you will not be able to restore any information saved up today."
                                  />
                                  <UserData
                                    userschema={userschema}
                                    user={user}
                                  />
                                </ModalContent>
                                <ModalActions>
                                  <Button
                                    className="cancel-color"
                                    onClick={() => setOpen(false)}
                                  >
                                    <FormattedMessage
                                      id="Cancel"
                                      defaultMessage="Cancel"
                                    />
                                  </Button>
                                  <Button
                                    className="rejected-color"
                                    content={intl.formatMessage(
                                      messages.yesDelete,
                                    )}
                                    onClick={() => {
                                      dispatch(deleteUser(user.id));
                                      setOpen(false);
                                    }}
                                  />
                                </ModalActions>
                              </Modal>
                            </TabPane>
                          ),
                        },
                      ]}
                    />
                  </Accordion.Content>
                </div>
              );
            })}
      </Accordion>
    </>
  );
};
